<script setup>
import {computed, ref, watch} from "vue";
import SpecificLocationStep from "@/components/ai-onboarding/steps/SpecificLocationStep.vue";
import NavigationButtons from "@/components/ai-onboarding/partials/NavigationButtons.vue";
import {changeStep, Steps} from "@/composables/ai-onboarding/useSteps";
import LocationSuggestionBox from "@/components/ai-onboarding/partials/LocationSuggestionBox.vue";
import FadeTransition from "@/components/transitions/FadeTransition.vue";
import axios from "axios";
import {useStore} from "vuex";
import AcknowledgementModal from "@/components/kit/AcknowledgementModal.vue";

const emit = defineEmits(['changeStep']);
const specificLocationStep = ref(null);

const store = useStore();

const selectedLocation = computed(() => store.getters['aiOnboarding/selectedLocationStep']);
const showSpecificLocationStep = computed(() => store.getters['aiOnboarding/showSpecificLocationStep']);
const loading = ref(false);

const props = defineProps({
  location: {
    type: Object,
    required: true,
  },
});

const locationName = computed(() => {
  if (props.location.the_prefix) {
    return `the ${props.location.loc_name}`;
  }

  return props.location.loc_name;
});

const toggleSelectedLocation = (location) => {
  if (selectedLocation.value === location) {
    store.dispatch('aiOnboarding/setSelectedLocationStep', '');
    return;
  }

  store.dispatch('aiOnboarding/setSelectedLocationStep', location);
}

const handleBack = () => {
  selectedLocations.value = [];
  store.dispatch('aiOnboarding/setSelectedLocationStep', '');
}

const showAcknowledgementModal = ref(false);

const toggleShowAcknowledgementModal = () => {
  showAcknowledgementModal.value = !showAcknowledgementModal.value;
}

const emitNextStep = async () => {
  showAcknowledgementModal.value = false;

  loading.value = true;

  if (selectedLocation.value === 'specific') {
    await specificLocationStep.value.handleContinue();

    loading.value = false;

    changeStep(Steps.value.BUSINESS_DESCRIPTION, emit);
    return;
  }

  await axios.post('/ajax/v2/ai-onboarding/locations', {
    locations: null,
  });

  loading.value = false;

  changeStep(Steps.value.BUSINESS_DESCRIPTION, emit);
}

const selectedLocations = ref([]);

const shouldDisableContinue = computed(() => {
  if (selectedLocation.value === 'country') {
    return false;
  }

  return selectedLocations.value.length <= 0;
});

watch(selectedLocation, (newValue) => {
  if (newValue !== 'specific') {
    store.dispatch('aiOnboarding/setShowSpecificLocationStep', false);
  }

  // delay for a bit to show the selected stage
  if (newValue === 'specific') {
    setTimeout(() => {
      store.dispatch('aiOnboarding/setShowSpecificLocationStep', true);
    }, 500);
  }
});
</script>

<template>
  <div class="tw-flex tw-flex-col tw-w-full tw-justify-between tw-h-full">
    <div class="tw-text-center tw-px-14 tw-py-18 tw-overflow-y-auto visible-scrollbar">
      <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-8">
        <FadeTransition>
          <SpecificLocationStep
            v-if="showSpecificLocationStep"
            ref="specificLocationStep"
            v-model="selectedLocations"
            class="tw-pt-4"
          />

          <div
            v-else
            class="tw-flex tw-gap-8 tw-pt-4"
          >
            <LocationSuggestionBox
              :title="`Whole of ${locationName}`"
              icon="icons/earth-pin"
              :selected="selectedLocation === 'country'"
              :disabled="selectedLocation === 'specific'"
              @select-option="toggleSelectedLocation('country')"
            />

            <LocationSuggestionBox
              :title="`Specific locations within ${locationName}`"
              icon="icons/map-pin"
              :selected="selectedLocation === 'specific'"
              :disabled="selectedLocation === 'country'"
              @select-option="toggleSelectedLocation('specific')"
            />
          </div>
        </FadeTransition>
      </div>
    </div>

    <NavigationButtons
      :show-back-button="selectedLocation === 'specific'"
      :disable-continue="shouldDisableContinue"
      :loading="loading"
      @back="handleBack"
      @next="toggleShowAcknowledgementModal"
    />

    <AcknowledgementModal
      v-if="showAcknowledgementModal"
      heading="Double-check your locations before moving on 🚦"
      sub-text="The locations you choose will directly shape the SEO strategy I create for you. Before you move on to the next step, can you confirm the locations are correct?"
      cancel-text="I want to double-check quickly"
      confirm-text="Yes, I'm happy with these locations"
      @close="toggleShowAcknowledgementModal"
      @confirm="emitNextStep"
    />
  </div>
</template>
